import { useChatShow } from '@jobilla/nexus-sdk'
import { UIMessage } from 'ai'
import { useMemo } from 'react'

function isNotFoundError(error: unknown): boolean {
	const err = error as { status?: number; response?: { status?: number } } | null
	return err?.status === 404 || err?.response?.status === 404
}

export function useChatHistory(chatId: string) {
	const { data: chatHistory, refetch, isLoading, isPending, error, isError } = useChatShow(
		chatId,
		{},
		{
			enabled: Boolean(chatId),
			staleTime: Infinity,
			refetchOnMount: 'always',
			retry: (failureCount, error) => {
				if (isNotFoundError(error)) { return false }
				return failureCount < 3
			},
		},
	)

	const messages = useMemo<UIMessage[]>(() => {
		if (isError && isNotFoundError(error)) {
			return []
		}

		if (!chatHistory?.messages) {
			return []
		}

		// Parts are AI-SDK message parts persisted as opaque JSON, so the union
		// cannot be expressed in the spec and is reconstructed here.
		return chatHistory.messages.map((message) => ({
			id: message.id,
			role: (message.role === 'tool' ? 'assistant' : message.role) as UIMessage['role'],
			parts: message.parts as UIMessage['parts'],
			metadata: message.metadata,
		}))
	}, [chatHistory, isError, error])

	const isLoadingChatHistory = isLoading || isPending

	return { messages, refetch, isLoadingChatHistory }
}
